import React, { useState } from 'react';
import { Check, CheckCheck, ChevronDown, ChevronRight, EyeOff, Lightbulb } from 'lucide-react';
import { readOnlyTitle } from '../../services/readOnlyNotice';
import {
    uebernehmbareVorschlaege, vorschlagFeldLabel, vorschlagWertText, type PlanungVorschlag,
} from './planungVorbelegung';

interface Props {
    /** Alle Vorschlaege aus `vorschlaegeAusProjekt` — die Leiste blendet selbst aus. */
    vorschlaege: readonly PlanungVorschlag[];
    /** In dieser Sitzung ausgeblendete Ids. Haelt das Modul, damit ein
     *  Reiterwechsel sie nicht vergisst; gespeichert wird nichts. */
    ausgeblendet: ReadonlySet<string>;
    isReadOnly: boolean;
    onUebernehmen: (vorschlag: PlanungVorschlag) => void;
    onAlleUebernehmen: (liste: PlanungVorschlag[]) => void;
    onAusblenden: (id: string) => void;
}

/**
 * Die Vorschlagsleiste ueber den Grundlagen: „Vorschläge aus den Projektdaten".
 *
 * Sie zeigt nur an und ruft auf. Welche Vorschlaege es gibt und was eine
 * Uebernahme am Entwurf aendert, steht geprueft in `planungVorbelegung.ts`.
 *
 * Drei Zusagen, die die Leiste dem Anwender macht:
 *  - Nichts wird von selbst gesetzt. Jede Zeile nennt Wert, Quelle und
 *    Rechenweg; uebernommen wird per Klick in den ENTWURF, gespeichert
 *    wird er automatisch (`autoSpeichern.ts`).
 *  - Ein Vorschlag verschwindet, sobald das Feld gesetzt ist — gleich ob
 *    durch Uebernahme oder durch eigene Eingabe.
 *  - „Ausblenden" gilt nur fuer diese Sitzung (Entscheid: verworfene
 *    Vorschlaege werden nicht gemerkt). Beim naechsten Oeffnen steht der
 *    Vorschlag wieder da, solange das Feld leer ist.
 */
export const PlanungVorschlagsLeiste: React.FC<Props> = ({
    vorschlaege, ausgeblendet, isReadOnly, onUebernehmen, onAlleUebernehmen, onAusblenden,
}) => {
    const [offen, setOffen] = useState(true);

    const sichtbar = vorschlaege.filter(v => !ausgeblendet.has(v.id));
    if (sichtbar.length === 0) return null;
    const uebernehmbar = uebernehmbareVorschlaege(sichtbar);

    const knopf = 'inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-md border '
        + 'disabled:opacity-40 disabled:cursor-not-allowed';

    return (
        <section className="bg-blue-50 border border-blue-200 rounded-2xl mb-6">
            <div className="flex items-center gap-2 px-4 py-2.5">
                <button
                    type="button"
                    onClick={() => setOffen(o => !o)}
                    className="flex items-center gap-2 min-w-0 flex-1 text-left"
                    aria-expanded={offen}
                >
                    {offen ? <ChevronDown className="w-4 h-4 text-blue-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-blue-500 shrink-0" />}
                    <Lightbulb className="w-4 h-4 text-blue-600 shrink-0" />
                    <span className="text-sm font-bold text-blue-900 truncate">
                        Vorschläge aus den Projektdaten ({sichtbar.length})
                    </span>
                </button>
                {uebernehmbar.length > 1 && (
                    <button
                        type="button"
                        onClick={() => onAlleUebernehmen(uebernehmbar)}
                        disabled={isReadOnly}
                        title={isReadOnly ? readOnlyTitle('Übernehmen') : 'Alle sichtbaren Vorschläge in die Grundlagen übernehmen — wird automatisch gespeichert'}
                        className={`${knopf} text-white bg-blue-600 border-blue-600 hover:bg-blue-700 shrink-0`}
                    >
                        <CheckCheck className="w-3.5 h-3.5" /> Alle übernehmen ({uebernehmbar.length})
                    </button>
                )}
            </div>

            {offen && (
                <div className="border-t border-blue-200">
                    <p className="px-4 pt-2.5 text-[11px] text-blue-800 leading-snug">
                        Aus Stammdaten, Verteiler und LV abgeleitet — nichts davon ist gesetzt. Übernommen wird je
                        Zeile in die Grundlagen und automatisch gespeichert. „Ausblenden“ gilt nur
                        für diese Sitzung.
                    </p>
                    <ul className="divide-y divide-blue-100">
                        {sichtbar.map(v => (
                            <li key={v.id} className="px-4 py-2.5 flex items-start gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="text-xs text-slate-800 leading-snug">
                                        <span className="font-bold">{vorschlagFeldLabel(v)}</span>
                                        {v.art !== 'hinweis' && <span className="text-slate-400"> → </span>}
                                        {v.art !== 'hinweis'
                                            ? <span className="font-bold text-blue-700">{vorschlagWertText(v)}</span>
                                            : <span className="text-slate-700">: {vorschlagWertText(v, 400)}</span>}
                                    </div>
                                    <div className="text-[11px] text-slate-500 leading-snug mt-0.5">
                                        <span className="font-bold">Quelle:</span> {v.quelle}
                                    </div>
                                    {v.rechenweg && (
                                        <div className="text-[11px] text-slate-500 leading-snug mt-0.5">
                                            <span className="font-bold">Rechenweg:</span> {v.rechenweg}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    {v.art !== 'hinweis' && (
                                        <button
                                            type="button"
                                            onClick={() => onUebernehmen(v)}
                                            disabled={isReadOnly}
                                            title={isReadOnly ? readOnlyTitle('Übernehmen') : 'In den Entwurf übernehmen'}
                                            className={`${knopf} text-blue-700 bg-white border-blue-300 hover:bg-blue-100`}
                                        >
                                            <Check className="w-3.5 h-3.5" /> Übernehmen
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => onAusblenden(v.id)}
                                        disabled={isReadOnly}
                                        title={isReadOnly ? readOnlyTitle('Ausblenden') : 'Für diese Sitzung ausblenden — nichts wird gespeichert'}
                                        className={`${knopf} text-slate-500 bg-white border-slate-200 hover:bg-slate-50`}
                                    >
                                        <EyeOff className="w-3.5 h-3.5" /> Ausblenden
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
};
